import { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useI18n } from "@/i18n";
import { fill, COMPANY } from "@/content/company";
import { useLenis } from "@/components/site/useLenis";
import { Header } from "@/components/site/Header";
import { Footer, LEGAL_DOCS } from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";

export default function Legal() {
  useLenis();
  const { doc } = useParams();
  const { t } = useI18n();
  useEffect(() => {
    window.__lenis?.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, [doc]);

  const d = t.legal?.[doc];
  if (!d) return <Navigate to="/legal/imprint" replace />;
  const [y, m, day] = COMPANY.updated.split("-");

  return (
    <div className="grain min-h-screen bg-ink text-bone">
      <Header />
      <main className="wrap grid gap-12 pb-24 pt-32 lg:grid-cols-12 lg:pt-40">
        <aside className="lg:col-span-3">
          <div className="lg:sticky lg:top-32">
            <Link to="/" data-testid="legal-back-link" className="flex items-center gap-2 text-sm text-zinc-300 hover:text-signal"><ArrowLeft className="h-4 w-4" />{t.legalNav.back}</Link>
            <nav className="mt-8 flex flex-wrap gap-2 lg:flex-col lg:gap-1" aria-label={t.footer.legal}>
              {LEGAL_DOCS.map((k) => (
                <Link key={k} to={`/legal/${k}`} data-testid={`legal-nav-${k}`} aria-current={k === doc ? "page" : undefined}
                  className={`border px-3 py-2 text-sm transition-colors lg:border-0 lg:border-l-2 lg:px-4 ${k === doc ? "border-signal text-bone" : "border-white/10 text-zinc-400 hover:text-bone"}`}>
                  {t.legalNav[k]}
                </Link>
              ))}
            </nav>
          </div>
        </aside>
        <article data-testid={`legal-doc-${doc}`} className="max-w-3xl lg:col-span-9">
          <p className="eyebrow">{t.legalNav.updated}: {day}.{m}.{y}</p>
          <Reveal as="h1" key={`${doc}-h`} className="mt-6 font-display text-4xl font-semibold tracking-[-0.03em] sm:text-5xl lg:text-6xl">{d.title}</Reveal>
          <div className="mt-14 space-y-10">
            {d.sections.map((s) => (
              <section key={s.h} className="border-t border-white/10 pt-6">
                <h2 className="font-display text-xl font-semibold tracking-tight">{s.h}</h2>
                <div className="mt-3 space-y-3 text-zinc-300">
                  {s.p.map((p) => <p key={p.slice(0, 40)} className="leading-relaxed">{fill(p)}</p>)}
                </div>
              </section>
            ))}
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}
